import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Sermons', path: '/sermons' },
    { name: 'Events', path: '/events' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center space-x-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png"
              alt="Christ Refuge Community Church"
              className="h-14 w-auto group-hover:scale-105 transition-transform duration-300"
            />
            <div className="flex flex-col">
              <span className="text-xl font-heading font-bold text-foreground leading-tight">Christ Refuge</span>
              <span className="text-xs text-muted-foreground tracking-wide">Community Church</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-full font-body font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-card hover:text-primary'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:8168760470" 
              data-testid="phone-link"
              className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">(816) 876-0470</span>
            </a>
            <Link
              to="/give"
              data-testid="give-button"
              className="bg-accent text-accent-foreground px-6 py-2.5 rounded-full font-medium hover:bg-accent/90 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              Give
            </Link>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-card transition-colors duration-300"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="lg:hidden border-t border-border/50 bg-background"
          >
            <div className="px-4 py-6 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-xl font-body font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-card'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-border/50 space-y-3">
                <a 
                  href="tel:8168760470"
                  data-testid="mobile-phone-link" 
                  className="flex items-center space-x-2 px-4 py-3 text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">(816) 876-0470</span>
                </a>
                <Link
                  to="/give"
                  data-testid="mobile-give-button"
                  onClick={() => setIsOpen(false)}
                  className="block text-center bg-accent text-accent-foreground px-6 py-3 rounded-full font-medium hover:bg-accent/90 transition-all duration-300"
                >
                  Give
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
